import React from "react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { getFileByIdAPI } from "../../../api/userApi";
import cameraOverlayIcon from "../../../assets/mynaui_camera-solid.png";

interface ProfileBannerProps {
  profileBannerImage?: string | null;
  avatarImage?: string | null;
  isOwner?: boolean;
  onBannerSelect?: (file: File) => void;
  isLoading?: boolean;
  showEditButton?: boolean;
}

const useStyles = makeStyles(theme => ({
  root: {
    position: "relative",
    width: "100%",
    height: 220,
    overflow: "hidden",
    backgroundColor: "#1E1E1E",
    [theme.breakpoints.down("sm")]: {
      height: 160,
    },
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  placeholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9E9E9E",
    backgroundColor: "#2A2A2A",
    fontSize: "0.95rem",
  },
  editOverlayButton: {
    position: "absolute",
    right: theme.spacing(2),
    bottom: theme.spacing(2),
    width: 64,
    height: 64,
    borderRadius: "50%",
    border: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    backdropFilter: "blur(2px)",
    WebkitBackdropFilter: "blur(2px)",
    cursor: "pointer",
    padding: 0,
    zIndex: 3,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.36)",
    },
    [theme.breakpoints.down("xs")]: {
      width: 32,
      height: 32,
      right: theme.spacing(1),
      bottom: theme.spacing(1),
    },
  },
  editOverlayIcon: {
    width: 40,
    height: 40,
    objectFit: "contain",
    [theme.breakpoints.down("xs")]: {
      width: 20,
      height: 20,
    },
  },
  loadingBackdrop: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.35)",
  },
}));

const absoluteUrlPattern = /^(https?:|data:|blob:)/i;

const ProfileBanner: React.FC<ProfileBannerProps> = ({
  profileBannerImage,
  avatarImage,
  isOwner = false,
  onBannerSelect,
  isLoading = false,
  showEditButton = true,
}) => {
  const classes = useStyles();
  const [resolvedBannerSrc, setResolvedBannerSrc] = React.useState<
    string | null
  >(null);
  const [isFetching, setIsFetching] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    let isMounted = true;
    let objectUrl: string | null = null;

    const resolveBanner = async () => {
      if (!profileBannerImage) {
        if (isMounted) {
          setResolvedBannerSrc(null);
        }
        return;
      }

      if (absoluteUrlPattern.test(profileBannerImage)) {
        if (isMounted) {
          setResolvedBannerSrc(profileBannerImage);
        }
        return;
      }

      const fileId = profileBannerImage.split("/api/files/")[1];
      if (!fileId) {
        if (isMounted) {
          setResolvedBannerSrc(null);
        }
        return;
      }

      setIsFetching(true);
      try {
        const url = await getFileByIdAPI(fileId);
        objectUrl = url;
        if (isMounted) {
          setResolvedBannerSrc(url);
        }
      } catch (error) {
        console.error("Failed to fetch banner image", error);
        if (isMounted) {
          setResolvedBannerSrc(null);
        }
      } finally {
        if (isMounted) {
          setIsFetching(false);
        }
      }
    };

    resolveBanner();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [profileBannerImage]);

  const bannerSrc = resolvedBannerSrc || avatarImage || "";
  const loadingState = isLoading || isFetching;

  const handleEditClick = React.useCallback(() => {
    if (loadingState) return;
    fileInputRef.current?.click();
  }, [loadingState]);

  const handleFileChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file && onBannerSelect) {
        onBannerSelect(file);
      }
      // дозволяє вибрати той самий файл повторно
      event.target.value = "";
    },
    [onBannerSelect]
  );

  const renderPlaceholder = () => {
    if (!bannerSrc) {
      return (
        <Box className={classes.placeholder}>
          <Typography variant="body2" color="inherit">
            No banner image
          </Typography>
        </Box>
      );
    }
    return (
      <img src={bannerSrc} alt="Profile banner" className={classes.image} />
    );
  };

  return (
    <Box className={classes.root}>
      {renderPlaceholder()}

      {loadingState && (
        <Box className={classes.loadingBackdrop}>
          <CircularProgress size={32} />
        </Box>
      )}

      {isOwner && showEditButton && (
        <button
          type="button"
          className={classes.editOverlayButton}
          onClick={handleEditClick}
          disabled={loadingState}
          aria-label="Change banner photo"
        >
          <img
            src={cameraOverlayIcon}
            alt="Change banner"
            className={classes.editOverlayIcon}
          />
        </button>
      )}

      {isOwner && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      )}
    </Box>
  );
};

export default ProfileBanner;
